// Pure Game of Life utilities operating on a Uint8Array buffer.

export function liveNeighbors(
  buf: Uint8Array,
  x: number,
  y: number,
  cols: number,
  rows: number
): number {
  let n = 0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      n += buf[ny * cols + nx];
    }
  }
  return n;
}

export function stepConway(
  cells: Uint8Array,
  next: Uint8Array,
  cols: number,
  rows: number
): void {
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const n = liveNeighbors(cells, x, y, cols, rows);
      const idx = y * cols + x;
      const alive = cells[idx];
      next[idx] =
        (alive && (n === 2 || n === 3)) || (!alive && n === 3) ? 1 : 0;
    }
  }
}

export function seedRandomFill(
  buf: Uint8Array,
  cols: number,
  rows: number,
  density: number,
  rand: () => number
): void {
  for (let i = 0; i < cols * rows; i++) {
    buf[i] = rand() < density ? 1 : 0;
  }
}

// Known patterns
export function stampGlider(
  buf: Uint8Array,
  cx: number,
  cy: number,
  cols: number,
  rows: number
): void {
  const pattern = [
    [0, 1, 0],
    [0, 0, 1],
    [1, 1, 1],
  ];
  stamp(buf, pattern, cx, cy, cols, rows);
}

export function stampPulsar(
  buf: Uint8Array,
  cx: number,
  cy: number,
  cols: number,
  rows: number
): void {
  const pattern = [
    [0, 0, 1, 1, 1, 0, 0, 0, 1, 1, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1],
    [1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1],
    [1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1],
    [0, 0, 1, 1, 1, 0, 0, 0, 1, 1, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 0, 0, 0, 1, 1, 1, 0, 0],
    [1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1],
    [1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1],
    [1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 0, 0, 0, 1, 1, 1, 0, 0],
  ];
  stamp(buf, pattern, cx, cy, cols, rows);
}

export function stampLWSS(
  buf: Uint8Array,
  cx: number,
  cy: number,
  cols: number,
  rows: number
): void {
  // Light-weight spaceship
  const pattern = [
    [0, 1, 1, 1, 1],
    [1, 0, 0, 0, 1],
    [0, 0, 0, 0, 1],
    [1, 0, 0, 1, 0],
  ];
  stamp(buf, pattern, cx, cy, cols, rows);
}

export function stampGosperGun(
  buf: Uint8Array,
  cx: number,
  cy: number,
  cols: number,
  rows: number
): void {
  // Gosper Glider Gun (36×9)
  const coords: [number, number][] = [
    [0, 4], [0, 5], [1, 4], [1, 5],
    [10, 4], [10, 5], [10, 6], [11, 3], [11, 7], [12, 2], [12, 8],
    [13, 2], [13, 8], [14, 5], [15, 3], [15, 7], [16, 4], [16, 5], [16, 6], [17, 5],
    [20, 2], [20, 3], [20, 4], [21, 2], [21, 3], [21, 4], [22, 1], [22, 5],
    [24, 0], [24, 1], [24, 5], [24, 6],
    [34, 2], [34, 3], [35, 2], [35, 3],
  ];
  for (const [dx, dy] of coords) {
    const x = cx + dx;
    const y = cy + dy;
    if (x >= 0 && y >= 0 && x < cols && y < rows) {
      buf[y * cols + x] = 1;
    }
  }
}

function stamp(
  buf: Uint8Array,
  pattern: number[][],
  cx: number,
  cy: number,
  cols: number,
  rows: number
): void {
  const h = pattern.length;
  const w = pattern[0].length;
  const ox = cx - Math.floor(w / 2);
  const oy = cy - Math.floor(h / 2);
  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const x = ox + px;
      const y = oy + py;
      if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
      if (pattern[py][px]) buf[y * cols + x] = 1;
    }
  }
}

export function countLive(buf: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < buf.length; i++) n += buf[i];
  return n;
}
